<script>
  import { onMount } from 'svelte';
  import { user } from '../../lib/auth/auth.js';
  import { selectedOrgId, availableOrgs } from '../../lib/orgContext.js';
  import api from '../../lib/api.js';
  import { detectKeyType, describeKeyType, extractSaveError, KEY_TYPE } from '../../lib/vaultKeyHints.js';
  import {
    COINBASE, VENUES, isCoinbaseAdvanced, isCoinbaseSandbox, requiresPassphrase, venueName,
  } from '../../lib/venues.js';

  // Credentials are stored per VENUE (ADR-0040): the select offers exactly the registry's venues — Coinbase
  // Advanced (CBA, the real-money venue) and Coinbase Sandbox (CBX). Paper mode needs no credential, so
  // nothing here is "paper". A legacy account row on another venue still displays by `venueName`.

  // Placeholder shown instead of a stored secret. Never sent back to the server.
  const MASKED_SECRET = '*****';

  let selectedExchange = COINBASE;
  let accounts = [];
  let selectedAccount = null;
  let loading = false;
  let error = null;
  let successMessage = null;

  // Form state
  let accountId = null; // Will always be null for API calls
  let accountNickName = '';
  let apiKey = '';
  let apiSecret = '';
  let passphrase = '';
  let isEditing = false;

  // Live key-type hint for the Advanced Trade secret field (ADR-0022). A guess only — the server
  // re-derives the type authoritatively and verifies the key against Coinbase before storing it.
  // Suppressed while the loaded secret is masked, since '*****' is not key material.
  //
  // A recognized type is shown as soon as it is recognizable, but the "unrecognized" WARNING is
  // held back until the field is plausibly complete: a half-typed key is unrecognizable by
  // definition, and warning from the first keystroke would tell every editing user their valid key
  // is broken. The shortest real secret is a 44-char (32-byte) Ed25519 seed.
  $: detectedKeyType = isCoinbaseAdvanced(selectedExchange) && apiSecret !== MASKED_SECRET
    ? detectKeyType(apiSecret)
    : KEY_TYPE.EMPTY;
  $: keyTypeHint = detectedKeyType === KEY_TYPE.UNKNOWN && apiSecret.trim().length < 44
    ? null
    : describeKeyType(detectedKeyType);

  // Use selectedOrgId from org context, fallback to user's default org
  $: orgId = $selectedOrgId || $user?.id;
  $: orgName = $availableOrgs?.find(o => o.orgId === orgId)?.orgName || $user?.name || $user?.email;

  // Reload accounts when org changes
  $: if (orgId) {
    loadAccounts();
  }

  onMount(() => {
    if (orgId) {
      loadAccounts();
    }
  });

  async function loadAccounts() {
    if (!orgId) return;

    loading = true;
    error = null;

    try {
      accounts = await api.getVaultAccounts(orgId);
    } catch (err) {
      console.error('Failed to load accounts:', err);
      error = 'Failed to load accounts. Please try again.';
    } finally {
      loading = false;
    }
  }

  async function loadAccountCredentials(account) {
    if (!orgId) return;

    loading = true;
    error = null;

    try {
      const credentials = await api.getVaultSecret(
        orgId,
        account.exchgId,
        account.accountId
      );

      selectedAccount = account;
      accountId = account.accountId;
      accountNickName = account.accountNickName;
      selectedExchange = account.exchgId;
      apiKey = credentials.apiKey || '';
      apiSecret = MASKED_SECRET; // Never render the stored secret back into the DOM
      passphrase = credentials.passphrase || '';
      isEditing = true;
    } catch (err) {
      console.error('Failed to load credentials:', err);
      error = 'Failed to load credentials. Please try again.';
    } finally {
      loading = false;
    }
  }

  async function saveCredentials() {
    if (!orgId || !apiKey || !apiSecret) {
      error = 'Please fill in all required fields (API Key and Secret)';
      return;
    }

    if (apiSecret === MASKED_SECRET) {
      error = 'Please enter the actual API secret to save';
      return;
    }

    loading = true;
    error = null;
    successMessage = null;

    try {
      const credentials = {
        APIKey: apiKey,
        APISecret: apiSecret,
        Passphrase: passphrase || ''
      };

      await api.setVaultSecret(
        orgId,
        selectedExchange,
        null, // Pass null for accountId
        accountNickName || null, // Pass null if nickname is empty
        credentials
      );

      // The server verified the credential against Coinbase before storing it, so this is a real
      // "it works", not just "it saved" (ADR-0022 §5).
      successMessage = 'Credentials saved and verified with Coinbase.';
      setTimeout(() => successMessage = null, 3000);

      // Reload accounts and reset form
      await loadAccounts();
      resetForm();
    } catch (err) {
      // Surface the server's actual objection ("Coinbase rejected this Ed25519 credential…",
      // "the key lacks the View permission") rather than a generic retry prompt that hides it.
      error = extractSaveError(err);
    } finally {
      loading = false;
    }
  }

  async function deleteCredentials(account) {
    if (!orgId) return;

    if (!confirm(`Are you sure you want to delete credentials for ${account.accountNickName}?`)) {
      return;
    }

    loading = true;
    error = null;
    successMessage = null;

    try {
      await api.deleteVaultSecret(
        orgId,
        account.exchgId,
        account.accountId
      );

      successMessage = 'Credentials deleted successfully!';
      setTimeout(() => successMessage = null, 3000);

      // Reload accounts and reset form if the deleted account was selected
      await loadAccounts();
      if (selectedAccount?.accountId === account.accountId) {
        resetForm();
      }
    } catch (err) {
      console.error('Failed to delete credentials:', err);
      error = 'Failed to delete credentials. Please try again.';
    } finally {
      loading = false;
    }
  }

  function resetForm() {
    selectedAccount = null;
    accountId = null;
    accountNickName = '';
    apiKey = '';
    apiSecret = '';
    passphrase = '';
    isEditing = false;
    selectedExchange = COINBASE;
  }
</script>

<div class="vault-management">
  <div class="vault-header">
    <h2 class="vault-title">Exchange Credentials</h2>
  </div>

  {#if error}
    <div class="alert alert-danger">
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <circle cx="10" cy="10" r="9" stroke="currentColor" stroke-width="2"/>
        <path d="M10 6v4M10 14v0.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
      </svg>
      {error}
      <button class="alert-close" on:click={() => error = null}>×</button>
    </div>
  {/if}

  {#if successMessage}
    <div class="alert alert-success">
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <circle cx="10" cy="10" r="9" stroke="currentColor" stroke-width="2"/>
        <path d="M6 10l3 3 5-6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
      </svg>
      {successMessage}
    </div>
  {/if}

  <div class="vault-content">
    <div class="vault-form-section">
      <div class="card">
        <div class="card-header">
          <h3 class="card-title">
            {isEditing ? 'Edit Credentials' : 'Add New Credentials'}
          </h3>
          {#if isEditing}
            <button class="btn btn-ghost btn-sm" on:click={resetForm}>
              Cancel
            </button>
          {/if}
        </div>

        <div class="card-body">
          <form on:submit|preventDefault={saveCredentials}>
            <div class="form-group">
              <label for="exchange" class="form-label">Exchange</label>
              <select
                id="exchange"
                class="form-select"
                bind:value={selectedExchange}
                disabled={isEditing}
              >
                {#each VENUES as venue (venue.id)}
                  <option value={venue.id}>{venue.name}</option>
                {/each}
              </select>
              <span class="form-hint">
                Coinbase Advanced is the real-money venue; Coinbase Sandbox holds your isolated fake-funds keys
                (from public.sandbox.exchange.coinbase.com). Paper trading needs no credential.
              </span>
            </div>

            <div class="form-group">
              <label for="accountNickName" class="form-label">Account Nickname</label>
              <input
                id="accountNickName"
                type="text"
                class="form-input"
                placeholder="e.g., Main Trading Account (optional)"
                bind:value={accountNickName}
              />
              <span class="form-hint">Optional friendly name to identify this account</span>
            </div>

            <div class="form-group">
              <label for="apiKey" class="form-label">API Key (Name) *</label>
              <input
                id="apiKey"
                type="text"
                class="form-input"
                placeholder="Enter API key name"
                bind:value={apiKey}
                required
              />
              {#if isCoinbaseAdvanced(selectedExchange)}
                <span class="form-hint">
                  Coinbase shows this as the key's name, usually
                  <code>organizations/&#123;org_id&#125;/apiKeys/&#123;key_id&#125;</code>.
                </span>
              {/if}
            </div>

            <div class="form-group">
              <label for="apiSecret" class="form-label">API Secret (Private Key) *</label>
              <textarea
                id="apiSecret"
                class="form-textarea"
                placeholder={isCoinbaseAdvanced(selectedExchange)
                  ? 'Paste your Ed25519 secret, or a legacy ECDSA private key'
                  : 'Enter or paste your private key'}
                bind:value={apiSecret}
                rows="4"
                required
              ></textarea>
              {#if keyTypeHint}
                <span class="key-type-hint key-type-hint--{keyTypeHint.tone}">
                  <strong>{keyTypeHint.label}</strong> {keyTypeHint.hint}
                </span>
              {/if}
              <span class="form-hint">
                {#if isEditing}
                  Secret is masked. Enter the actual secret to update it.
                {:else if isCoinbaseAdvanced(selectedExchange)}
                  Both Ed25519 (current) and ECDSA (legacy) keys work — K2 detects which you pasted.
                  New keys from the Coinbase developer portal are Ed25519. The credential is verified
                  with Coinbase when you save, and stored encrypted in Azure Key Vault.
                {:else}
                  Keep this secret secure. It will be encrypted in Azure Key Vault.
                {/if}
              </span>
            </div>

            {#if !isCoinbaseAdvanced(selectedExchange)}
              <div class="form-group">
                <label for="passphrase" class="form-label">
                  Passphrase {requiresPassphrase(selectedExchange) ? '*' : '(Optional)'}
                </label>
                <input
                  id="passphrase"
                  type="password"
                  class="form-input"
                  placeholder={isCoinbaseSandbox(selectedExchange) ? 'Sandbox API passphrase' : 'Enter passphrase if required'}
                  bind:value={passphrase}
                  required={requiresPassphrase(selectedExchange)}
                />
                <span class="form-hint">
                  {isCoinbaseSandbox(selectedExchange)
                    ? 'The Coinbase Exchange sandbox requires the passphrase shown when the API key was created.'
                    : 'Some exchanges require a passphrase'}
                </span>
              </div>
            {/if}

            <div class="form-actions">
              <button
                type="submit"
                class="btn btn-primary"
                disabled={loading}
              >
                {#if loading}
                  <span class="spinner"></span> Saving...
                {:else}
                  {isEditing ? 'Update Credentials' : 'Save Credentials'}
                {/if}
              </button>
              {#if isEditing}
                <button
                  type="button"
                  class="btn btn-ghost"
                  on:click={resetForm}
                >
                  Cancel
                </button>
              {/if}
            </div>
          </form>
        </div>
      </div>
    </div>

    <div class="vault-list-section">
      <div class="card">
        <div class="card-header">
          <h3 class="card-title">Saved Credentials</h3>
          <button
            class="btn btn-ghost btn-sm"
            on:click={loadAccounts}
            disabled={loading}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M14 8a6 6 0 11-12 0 6 6 0 0112 0z" stroke="currentColor" stroke-width="1.5"/>
              <path d="M8 4v4l2 2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
            Refresh
          </button>
        </div>

        <div class="card-body">
          {#if loading && accounts.length === 0}
            <div class="vault-loading">
              <span class="spinner"></span>
              <p>Loading accounts...</p>
            </div>
          {:else if accounts.length === 0}
            <div class="empty-state">
              <div class="empty-state-icon">
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                  <rect x="8" y="12" width="32" height="24" rx="2" stroke="currentColor" stroke-width="2"/>
                  <path d="M24 20v8M20 24h8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
              </div>
              <h4 class="empty-state-title">No credentials saved</h4>
              <p class="empty-state-description">Add your first exchange credentials to get started</p>
            </div>
          {:else}
            <div class="vault-accounts-list">
              {#each accounts as account (account.key)}
                <div class="vault-account-item">
                  <div class="vault-account-info">
                    <div class="vault-account-header">
                      <span class="vault-account-nickname">
                        {account.accountNickName || 'Exchange Account'}
                      </span>
                      <span class="badge badge-primary">{venueName(account.exchgId)}</span>
                    </div>
                  </div>
                  <div class="vault-account-actions">
                    <button
                      class="btn btn-ghost btn-sm"
                      on:click={() => loadAccountCredentials(account)}
                      disabled={loading}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M2 10.5V14h3.5l8.5-8.5-3.5-3.5L2 10.5z" stroke="currentColor" stroke-width="1.5"/>
                        <path d="M11.5 3.5l3.5 3.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                      </svg>
                      Edit
                    </button>
                    <button
                      class="btn btn-danger btn-sm"
                      on:click={() => deleteCredentials(account)}
                      disabled={loading}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M3 4h10M5 4V3a1 1 0 011-1h4a1 1 0 011 1v1M6 7v5M10 7v5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                        <path d="M4 4h8v9a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" stroke="currentColor" stroke-width="1.5"/>
                      </svg>
                      Delete
                    </button>
                  </div>
                </div>
              {/each}
            </div>
          {/if}
        </div>
      </div>
    </div>
  </div>
</div>

<style>
  @import './VaultManagement.css';
</style>
